import { motionDisabled } from '@/shared/lib/motion';
import { useEffect, useRef, useState } from 'react';

const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

/**
 * מספר "סופר" לערך היעד (עיצוב v2: KPI נספרים מ-0 ב-1.1ש, easeOutCubic).
 * בשינוי ערך — ממשיך מהערך המוצג הנוכחי ולא מאפס, כדי שעדכון חי כל כמה
 * שניות לא יקפיץ את המספר ל-0.
 */
export function useCountUp(target: number, ms = 1100): number {
  const reduce = motionDisabled();
  const [val, setVal] = useState(reduce ? target : 0);
  const fromRef = useRef(reduce ? target : 0);
  const valRef = useRef(val);
  valRef.current = val;

  useEffect(() => {
    if (!Number.isFinite(target)) { setVal(target); return; }
    if (reduce || ms <= 0) { setVal(target); return; }
    fromRef.current = valRef.current;
    const from = fromRef.current;
    if (from === target) return;
    let raf = 0;
    const t0 = performance.now();
    const step = (t: number) => {
      const p = Math.min(1, (t - t0) / ms);
      setVal(from + (target - from) * easeOutCubic(p));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, ms, reduce]);

  return val;
}

/** 0→1 פעם אחת בעלייה (או כשהמפתח משתנה) — לגרפים/פסים שנבנים. */
export function useEntrance(key: unknown = 'once', ms = 1100): number {
  const reduce = motionDisabled();
  const [p, setP] = useState(reduce ? 1 : 0);
  useEffect(() => {
    if (reduce) { setP(1); return; }
    setP(0);
    let raf = 0;
    const t0 = performance.now();
    const step = (t: number) => {
      const x = Math.min(1, (t - t0) / ms);
      setP(easeOutCubic(x));
      if (x < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [key, ms, reduce]);
  return p;
}
